import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Calendar as CalIcon, ChevronRight } from "lucide-react";
import SpanishDatePicker from "@/components/SpanishDatePicker";
import { phaseBadge, phaseLabel } from "@/lib/seasonPhase";
import { toast } from "sonner";

export default function TrainingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selTeam, setSelTeam] = useState("");
  const [trainings, setTrainings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", time: "", notes: "", season_phase: "" });
  const [dateRange, setDateRange] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    return { from: qs.get("date_from") || "", to: qs.get("date_to") || "" };
  });
  const [exactDate, setExactDate] = useState("");
  // Fecha de hoy según la zona horaria de Madrid (no UTC)
  const madridToday = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Madrid" }).format(new Date());

  useEffect(() => {
    api.get("/teams").then((r) => {
      setTeams(r.data);
      const qp = new URLSearchParams(window.location.search).get("team_id");
      const saved = localStorage.getItem("app_team_filter");
      if (r.data.length) {
        setSelTeam((s) => {
          if (s) return s;
          if (qp && r.data.some((t) => t.id === qp)) return qp;
          if (saved && saved !== "__all__" && r.data.some((t) => t.id === saved)) return saved;
          return r.data[0].id;
        });
      }
    });
  }, []);

  const pickTeam = (v) => {
    setSelTeam(v);
    localStorage.setItem("app_team_filter", v);
  };

  const load = () => {
    if (!selTeam) return;
    api.get(`/trainings?team_id=${selTeam}`).then((r) => setTrainings(r.data));
  };
  useEffect(() => { load(); }, [selTeam]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/trainings", { team_id: selTeam, date: form.date, time: form.time, notes: form.notes, season_phase: form.season_phase || null });
      toast.success("Entrenamiento creado");
      setForm({ date: "", time: "", notes: "", season_phase: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar entrenamiento del ${t.date}?`)) return;
    await api.delete(`/trainings/${t.id}`);
    toast.success("Eliminado");
    load();
  };

  const canEdit = user?.role === "admin" || (user?.team_ids || []).includes(selTeam);
  const teamName = teams.find((t) => t.id === selTeam)?.name || "";
  const hasDateFilter = Boolean(dateRange.from || dateRange.to);
  const visibleTrainings = trainings
    .filter((t) => {
      const d = String(t.date).slice(0, 10);
      if (exactDate && d !== exactDate) return false;
      if (dateRange.from && d < dateRange.from) return false;
      if (dateRange.to && d > dateRange.to) return false;
      return true;
    })
    .sort((a, b) => (a.date === b.date ? (a.time || "").localeCompare(b.time || "") : (a.date < b.date ? 1 : -1)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>Entrenamientos</h1>
          <p className="text-slate-500 text-sm mt-1">
            Registra las sesiones y pasa la asistencia.
            {teamName && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-bold" data-testid="trainings-team-name">
                {teamName}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {teams.length > 0 && (
            <Select value={selTeam} onValueChange={pickTeam}>
              <SelectTrigger className="w-56" data-testid="trainings-team-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canEdit && selTeam && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-training-button">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuevo entrenamiento</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  <div>
                    <Label>Fecha</Label>
                    <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="training-date-input" />
                  </div>
                  <div>
                    <Label>Hora</Label>
                    <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="training-time-input" />
                  </div>
                  <div>
                    <Label>Notas</Label>
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opcional" data-testid="training-notes-input" />
                  </div>
                  <div>
                    <Label>Fase</Label>
                    <Select value={form.season_phase || "auto"} onValueChange={(v) => setForm({ ...form, season_phase: v === "auto" ? "" : v })}>
                      <SelectTrigger data-testid="training-phase-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automática (según fechas del equipo)</SelectItem>
                        <SelectItem value="PRESEASON">Pretemporada</SelectItem>
                        <SelectItem value="COMPETITION">Competición</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="training-save-button">Crear</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="trainings-date-filter-bar">
        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Fecha</Label>
        <SpanishDatePicker
          value={exactDate}
          onChange={setExactDate}
          placeholder="Elegir fecha"
          testId="trainings-exact-date"
        />
        <Button variant="outline" size="sm" onClick={() => setExactDate(madridToday())} data-testid="trainings-date-today">
          Hoy
        </Button>
        {exactDate && (
          <Button variant="ghost" size="sm" className="text-xs text-purple-600 hover:bg-purple-50"
            onClick={() => setExactDate("")} data-testid="trainings-date-clear">
            Limpiar fecha
          </Button>
        )}
        {exactDate && (
          <span className="text-xs font-bold text-purple-800 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5" data-testid="trainings-exact-date-chip">
            {exactDate.split("-").reverse().join("/")}
          </span>
        )}
      </div>

      {hasDateFilter && (
        <div className="flex items-center gap-2 text-xs bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 w-fit" data-testid="trainings-date-filter-chip">
          <span className="font-bold text-purple-800">Filtro de fechas:</span>
          <span className="text-slate-600">{dateRange.from || "…"} → {dateRange.to || "…"}</span>
          <button
            type="button"
            onClick={() => setDateRange({ from: "", to: "" })}
            className="ml-1 font-bold text-purple-700 hover:text-purple-900"
            data-testid="trainings-date-filter-clear"
          >
            ✕ Quitar
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTrainings.length === 0 && (
          <div className="text-slate-500 text-sm col-span-full" data-testid="trainings-empty-msg">
            {exactDate
              ? "No hay entrenamientos registrados para la fecha seleccionada."
              : hasDateFilter ? "No hay entrenamientos en ese rango de fechas." : "Aún no hay entrenamientos."}
          </div>
        )}
        {visibleTrainings.map((t) => (
          <Card key={t.id} className="border-slate-200 shadow-sm hover:-translate-y-1 transition-transform cursor-pointer" onClick={() => navigate(`/trainings/${t.id}`)} data-testid={`training-card-${t.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-sky-100 text-sky-700 grid place-items-center">
                <CalIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black" style={{ fontFamily: "Nunito" }}>
                  {t.date}{t.time ? ` · ${t.time}` : ""}
                </div>
                <span className={`inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold ${phaseBadge(t.season_phase)}`} data-testid={`training-phase-badge-${t.id}`}>
                  {phaseLabel(t.season_phase)}
                </span>
                {teamName && <div className="text-xs font-bold text-purple-700">{teamName}</div>}
                <div className="text-sm text-slate-500 truncate">{t.notes || "Sin notas"}</div>
              </div>
              <div className="flex items-center gap-1">
                {canEdit && (
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(t); }} data-testid={`delete-training-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
